import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { Tabs, TabsContent } from "../components/ui/tabs";
import { SalesDocList } from "./sales/SalesDocList";
import { PostedDocuments } from "./sales/PostedDocuments";
import { Backorders } from "./sales/Backorders";
import { Customers } from "./sales/Customers";

export default function Sales() {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "quotes";
  const [colSort, setColSort] = useState(null);
  const [colFilters, setColFilters] = useState({});
  const setTab = (v) => { const p = new URLSearchParams(searchParams); p.set("tab", v); p.delete("sub"); setSearchParams(p, { replace: true }); setColSort(null); setColFilters({}); };
  const [statusFilter, setStatusFilter] = useState("all");
  const activeTabs = ["quotes", "orders", "returns", "invoices", "creditmemos"];

  const { data: views = [] } = useQuery({ queryKey: ["saved-views", "sales"], queryFn: async () => (await api.get("/saved-views?module=sales")).data });
  const saveView = useMutation({
    mutationFn: async () => {
      const name = window.prompt("Name this view", `${tab} · ${statusFilter}`);
      if (!name) return null;
      return (await api.post("/saved-views", { name, module: "sales", tab, status_filter: statusFilter, sort: colSort, filters: colFilters })).data;
    },
    onSuccess: (r) => { if (r) { qc.invalidateQueries({ queryKey: ["saved-views"] }); toast.success("View saved"); } },
  });
  const delView = useMutation({
    mutationFn: async (id) => api.delete(`/saved-views/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["saved-views"] }); },
  });
  const salesViews = views.filter((v) => v.module === "sales");

  return (
    <div className="animate-fade-up">
      {salesViews.length > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="saved-views-bar">
          <span className="overline-label">Saved views</span>
          {salesViews.map((v) => (
            <span key={v.id} className="group inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full text-xs font-semibold border border-border bg-card hover:border-primary/40">
              <button data-testid="saved-view" onClick={() => { setTab(v.tab || "quotes"); setStatusFilter(v.status_filter || "all"); setColSort(v.sort || null); setColFilters(v.filters || {}); }}>{v.name}</button>
              <button onClick={() => delView.mutate(v.id)} data-testid="delete-view" className="text-muted-foreground hover:text-destructive p-0.5"><X size={12} /></button>
            </span>
          ))}
        </div>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        {activeTabs.includes(tab) && (
          <div className="flex justify-end mb-3">
            <button onClick={() => saveView.mutate()} data-testid="save-view-button" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline"><FloppyDisk size={14} /> Save current view</button>
          </div>
        )}
        <TabsContent value="quotes"><SalesDocList docType="Quote" variant="active" statusFilter={statusFilter} setStatusFilter={setStatusFilter} colSort={colSort} setColSort={setColSort} colFilters={colFilters} setColFilters={setColFilters} /></TabsContent>
        <TabsContent value="orders"><SalesDocList docType="Order" variant="active" statusFilter={statusFilter} setStatusFilter={setStatusFilter} colSort={colSort} setColSort={setColSort} colFilters={colFilters} setColFilters={setColFilters} /></TabsContent>
        <TabsContent value="backorders"><Backorders /></TabsContent>
        <TabsContent value="returns"><SalesDocList docType="ReturnOrder" variant="active" statusFilter={statusFilter} setStatusFilter={setStatusFilter} colSort={colSort} setColSort={setColSort} colFilters={colFilters} setColFilters={setColFilters} /></TabsContent>
        <TabsContent value="invoices"><SalesDocList docType="Invoice" variant="active" statusFilter={statusFilter} setStatusFilter={setStatusFilter} colSort={colSort} setColSort={setColSort} colFilters={colFilters} setColFilters={setColFilters} /></TabsContent>
        <TabsContent value="creditmemos"><SalesDocList docType="CreditMemo" variant="active" statusFilter={statusFilter} setStatusFilter={setStatusFilter} colSort={colSort} setColSort={setColSort} colFilters={colFilters} setColFilters={setColFilters} /></TabsContent>
        <TabsContent value="customers"><Customers /></TabsContent>
        <TabsContent value="posted"><PostedDocuments /></TabsContent>
      </Tabs>
    </div>
  );
}
